import React, { useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import {
  FiHome,
  FiMessageSquare,
  FiChevronDown,
  FiSend,
  FiSmartphone,
  FiFileText,
  FiBook,
  FiCpu,
  FiMessageCircle,
  FiZap,
  FiPlusCircle,
  FiBarChart2,
  FiUsers,
  FiBarChart,
  FiUser,
  FiClock,
  FiLock,
  FiTag,
  FiBell,
  FiDownload,
  FiPower,
  FiMenu,
} from "react-icons/fi";

// ── Menu structure ────────────────────────────────────────────────────────────
const menuStructure = [
  {
    name: "Dashboard",
    path: "/dashboard",
    icon: FiHome,
  },
  {
    name: "WhatsApp",
    icon: FiMessageSquare,
    children: [
      { name: "Send WhatsApp", path: "whatsapp/send", icon: FiSend },
      {
        name: "WhatsApp Onboarding",
        path: "/whatsapp/onboarding",
        icon: FiSmartphone,
      },
      {
        name: "WhatsApp Template",
        path: "whatsapp/templates",
        icon: FiFileText,
      },
      { name: "WhatsApp Api Docs", path: "/whatsapp/api-docs", icon: FiBook },
    ],
  },
  {
    name: "Chatbot",
    path: "/chatbot",
    icon: FiCpu,
  },
  {
    name: "Chat Management",
    path: "/chat-management",
    icon: FiMessageCircle,
  },
  {
    name: "Campaign",
    icon: FiZap,
    children: [
      {
        name: "Create Campaign",
        path: "/campaign/create",
        icon: FiPlusCircle,
      },
      { name: "Campaign Report", path: "/campaign/report", icon: FiBarChart2 },
      { name: "Contact List", path: "/campaign/contact-list", icon: FiUsers },
    ],
  },
  {
    name: "Analytics",
    path: "/analytics",
    icon: FiBarChart,
  },
  {
    name: "Account",
    icon: FiUser,
    children: [
      { name: "Profile", path: "/account/profile", icon: FiUser },
      {
        name: "Transaction History",
        path: "/account/transactions",
        icon: FiClock,
      },
      {
        name: "Change Password",
        path: "/account/change-password",
        icon: FiLock,
      },
      { name: "Price List", path: "/account/pricing", icon: FiTag },
    ],
  },
];

export default function Layout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Auto-open parent if child is active
  const [openMenus, setOpenMenus] = useState(() => {
    const opened = {};
    menuStructure.forEach((item) => {
      if (item.children) {
        const active = item.children.some((c) =>
          location.pathname.startsWith(c.path),
        );
        if (active) opened[item.name] = true;
      }
    });
    return opened;
  });

  const toggleMenu = (name) =>
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  const anyChildActive = (children) => children?.some((c) => isActive(c.path));

  const getCurrentTitle = () => {
    for (const item of menuStructure) {
      if (item.path && isActive(item.path)) return item.name;
      if (item.children) {
        for (const child of item.children) {
          if (isActive(child.path)) return child.name;
        }
      }
    }
    return "Dashboard";
  };

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ background: "#f5f6fa" }}
    >
      {/* ── SIDEBAR ──────────────────────────────────────────────────────── */}
      <aside
        className="flex flex-col flex-shrink-0 bg-white overflow-hidden transition-all duration-300"
        style={{
          width: sidebarOpen ? 252 : 0,
          boxShadow: "3px 0 24px rgba(0,0,0,0.07)",
        }}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
            }}
          >
            <FiMessageSquare size={17} className="text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-800 tracking-tight leading-tight">
              Chatboxx
            </p>
            <p className="text-[10px] text-gray-400 leading-tight">
              WhatsApp Platform
            </p>
          </div>
        </div>

        {/* Nav */}
        <nav
          className="flex-1 overflow-y-auto py-3 px-2.5 space-y-px"
          style={{ scrollbarWidth: "none" }}
        >
          {menuStructure.map((item) => {
            // ── Simple link ──
            if (!item.children) {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-150 whitespace-nowrap"
                  style={
                    active
                      ? {
                          background:
                            "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
                          color: "#fff",
                          boxShadow: "0 4px 12px rgba(37,211,102,0.3)",
                        }
                      : { color: "#1f2933" }
                  }
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = "";
                  }}
                >
                  <item.icon
                    size={16}
                    style={{
                      color: active ? "#fff" : "#9ca3af",
                      flexShrink: 0,
                    }}
                  />
                  {item.name}
                </Link>
              );
            }

            // ── Dropdown ──
            const isOpen = openMenus[item.name];
            const active = anyChildActive(item.children);

            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleMenu(item.name)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-150 whitespace-nowrap"
                  style={
                    active
                      ? { background: "#f0fdf4", color: "#16a34a" }
                      : { color: "#1f2933" }
                  }
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    if (!active)
                      e.currentTarget.style.background = active
                        ? "#f0fdf4"
                        : "";
                  }}
                >
                  <item.icon
                    size={16}
                    style={{
                      color: active ? "#16a34a" : "#374151",
                      flexShrink: 0,
                    }}
                  />
                  <span className="flex-1 text-left">{item.name}</span>
                  <FiChevronDown
                    size={13}
                    style={{
                      color: "#374151",
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                      flexShrink: 0,
                    }}
                  />
                </button>

                {/* Children — smooth open/close */}
                <div
                  style={{
                    maxHeight: isOpen ? "300px" : "0px",
                    overflow: "hidden",
                    transition: "max-height 0.25s ease",
                  }}
                >
                  <div
                    className="ml-3 mt-1 pl-3 space-y-px pb-1"
                    style={{ borderLeft: "2px solid #f3f4f6" }}
                  >
                    {item.children.map((child) => {
                      const childActive = isActive(child.path);
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12.5px] transition-all duration-150 whitespace-nowrap"
                          style={
                            childActive
                              ? {
                                  background: "#f0fdf4",
                                  color: "#16a34a",
                                  fontWeight: 600,
                                }
                              : { color: "#374151" }
                          }
                          onMouseEnter={(e) => {
                            if (!childActive)
                              e.currentTarget.style.background = "#f9fafb";
                            if (!childActive)
                              e.currentTarget.style.color = "#374151";
                          }}
                          onMouseLeave={(e) => {
                            if (!childActive)
                              e.currentTarget.style.background = "";
                            if (!childActive)
                              e.currentTarget.style.color = "#374151";
                          }}
                        >
                          <span
                            className="rounded-full flex-shrink-0"
                            style={{
                              width: 6,
                              height: 6,
                              background: childActive ? "#22c55e" : "#d1d5db",
                            }}
                          />
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* User card at bottom */}
        <div className="px-2.5 py-3 border-t border-gray-100 flex-shrink-0">
          <div
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl"
            style={{ background: "#f9fafb" }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
              style={{
                background: "linear-gradient(135deg, #25d366, #128c7e)",
              }}
            >
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-gray-700 truncate leading-tight">
                {user?.name || "User"}
              </p>
              <p className="text-[10px] text-gray-400 truncate leading-tight">
                {user?.email || ""}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* ── MAIN ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── HEADER — like chatboxx: title left, icons+logout right ──── */}
        <header
          className="bg-white flex-shrink-0 flex items-center justify-between px-5 py-3 gap-3"
          style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}
        >
          {/* Left */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 transition-colors"
              style={{ background: "#f9fafb", border: "1px solid #f3f4f6" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#f3f4f6")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#f9fafb")
              }
            >
              <FiMenu size={17} />
            </button>
            <h1 className="text-lg font-bold text-gray-800 tracking-tight">
              {getCurrentTitle()}
            </h1>
          </div>

          {/* Right — Credits + icons + logout (exactly like chatboxx top right) */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* INR Credits — green badge like chatboxx */}
            <div
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-white text-xs font-bold cursor-pointer select-none"
              style={{
                background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
                boxShadow: "0 4px 12px rgba(37,211,102,0.25)",
              }}
              onClick={() => navigate("/account/transactions")}
            >
              <span>💳</span>
              <span>INR : {user?.credits || "0"}</span>
            </div>

            {/* Download / Recharge */}
            <HeaderBtn
              icon={FiDownload}
              title="Recharge"
              onClick={() => navigate("/account/pricing")}
            />

            {/* Notifications */}
            <HeaderBtn
              icon={FiBell}
              title="Notifications"
              dot
              onClick={() => navigate("/notifications")}
            />

            {/* Profile */}
            <HeaderBtn
              icon={FiUser}
              title="Profile"
              onClick={() => navigate("/account/profile")}
            />

            {/* Logout — red power button like chatboxx */}
            <button
              onClick={handleLogout}
              title="Logout"
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all hover:opacity-90 active:scale-95"
              style={{
                background: "linear-gradient(135deg, #ff6b6b, #c0392b)",
                boxShadow: "0 4px 12px rgba(192,57,43,0.25)",
              }}
            >
              <FiPower size={16} />
            </button>
          </div>
        </header>

        {/* ── Announcement strip ─────────────────────────────────────────── */}
        <div
          className="flex-shrink-0 flex items-center gap-2 px-5 py-2 text-white text-[12px] cursor-pointer select-none"
          style={{
            background: "linear-gradient(90deg, #128c7e 0%, #25d366 100%)",
          }}
        >
          <FiZap size={12} className="flex-shrink-0 animate-pulse" />
          <span className="flex-1 truncate">
            Our Chat Platform lets you manage and respond to conversations in
            real-time.
          </span>
          <span className="flex-shrink-0 font-semibold text-white/90 hover:text-white transition-colors">
            Explore →
          </span>
        </div>

        {/* ── Page outlet ───────────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// ── Small reusable header icon button ────────────────────────────────────────
function HeaderBtn({ icon: Icon, title, onClick, dot }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 transition-all hover:text-gray-700 active:scale-95 relative"
      style={{ background: "#f9fafb", border: "1px solid #f3f4f6" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "#f9fafb")}
    >
      <Icon size={16} />
      {dot && (
        <span
          className="absolute rounded-full border-2 border-white"
          style={{
            width: 8,
            height: 8,
            background: "#ef4444",
            top: 6,
            right: 6,
          }}
        />
      )}
    </button>
  );
}
